export enum LogLevel {
  ERROR = "error",
  WARNING = "warning",
}

export abstract class AggregationError extends Error {
  aggregatorError?: string;
  chemicalId?: string;
  chemicalName?: string;
  logLevel: LogLevel;
  /**
   * Set logLevel to LogLevel.WARNING to log as a warning instead of an error.
   */
  constructor(message: string, logLevel: LogLevel = LogLevel.ERROR) {
    super(message);
    this.name = this.constructor.name;
    this.logLevel = logLevel;
  }

  /**
   * Names the chemical on an error that was thrown further down, where the
   * chemical wasn't in scope — `convert` only knows the two units.
   *
   * Subclasses whose `aggregatorError` names the chemical override this so the
   * persisted message picks the name up as well. Assigning `chemicalId` and
   * `chemicalName` directly instead leaves the user reading the anonymous
   * variant of the message while the attribution is only in the fields.
   */
  attributeToChemical(
    chemicalId: string | null | undefined,
    chemicalName?: string | null | undefined
  ): void {
    this.chemicalId = chemicalId ?? undefined;
    this.chemicalName = chemicalName ?? undefined;
  }
}

export class MissingDensity extends AggregationError {
  constructor(
    private readonly sourceUnit: string,
    private readonly targetUnit: string,
    chemicalId?: string,
    chemicalName?: string
  ) {
    super("Conversions between mass and volume require a density.");
    // create the RFC record for these chemicals
    this.logLevel = LogLevel.WARNING;
    this.attributeToChemical(chemicalId, chemicalName);
  }

  override attributeToChemical(
    chemicalId: string | null | undefined,
    chemicalName?: string | null | undefined
  ): void {
    super.attributeToChemical(chemicalId, chemicalName);
    // Keyed on the name, not the id: a caller can know which chemical failed
    // without having its name, and "for chemical: undefined" reads worse than
    // leaving it out.
    if (this.chemicalName) {
      this.aggregatorError = `We were unable to aggregate this chemical because we cannot convert ${this.sourceUnit} to ${this.targetUnit} for chemical: ${this.chemicalName} without a density.`;
    } else {
      this.aggregatorError = `We were unable to aggregate this chemical because we cannot convert ${this.sourceUnit} to ${this.targetUnit} without a density.`;
    }
  }
}

export class UnsupportedUnitConversion extends AggregationError {
  constructor(
    private readonly sourceUnit: string,
    private readonly targetUnit: string,
    chemicalId?: string,
    chemicalName?: string
  ) {
    super(`Conversions from ${sourceUnit} to ${targetUnit} are currently not supported.`);
    this.attributeToChemical(chemicalId, chemicalName);
  }

  override attributeToChemical(
    chemicalId: string | null | undefined,
    chemicalName?: string | null | undefined
  ): void {
    super.attributeToChemical(chemicalId, chemicalName);
    // See MissingDensity: keyed on the name so a nameless attribution doesn't
    // render "for chemical: undefined".
    if (this.chemicalName) {
      this.aggregatorError = `We were unable to aggregate this chemical because we cannot accurately convert ${this.sourceUnit} to ${this.targetUnit} at this time for chemical: ${this.chemicalName}.`;
    } else {
      // create the RFC record for these chemicals
      this.aggregatorError = `We were unable to aggregate this chemical because we cannot accurately convert ${this.sourceUnit} to ${this.targetUnit} at this time.`;
    }
  }
}

export class CouldNotDetermineUnit extends AggregationError {
  constructor(missingUnit: "from" | "to") {
    super(`Unable to determine ${missingUnit}Unit`);
    this.logLevel = LogLevel.WARNING;
    this.aggregatorError = `We were unable to aggregate this chemical because we cannot determine the unit to convert ${missingUnit}.`;
  }
}
